import type {
  ContainerStage,
  FloraDefinition,
  FloraGeometryForm,
  SeasonalAppearance,
} from '@/flora/FloraDefinition';
import { computeGrowthState } from '@/flora/FloraGrowthModel';
import type { SimpleSeason } from '@/environment/EnvironmentState';
import { SIMPLE_SEASONS } from '@/environment/EnvironmentState';
import {
  ecologyScore,
  foodResourceLabel,
  habitatResourceLabel,
  wildlifeGroupLabel,
} from '@/flora/FloraEcology';
import type { ResolvedFlora } from '@/localization/FloraResolver';
import { nativeStatusLabel, primaryWarning, suitabilityLabel } from '@/localization/Suitability';
import { formatLength } from './dom';
import { estimateAgeYears, formatAge, formatDayOfYear } from './PlantMetrics';
import {
  COLOR_FAMILY_SWATCHES,
  categoryLabel,
  colorFamiliesOf,
  colorFamilyLabel,
  formFactorLabel,
  formFactorOf,
  fullDisplayName,
  genusOf,
  speciesEpithetOf,
  type ColorFamily,
  type FormFactor,
} from './FloraFacets';

/**
 * The plant faceplate: one {@link FloraDefinition} read back as a specification sheet.
 *
 * The palette tile shows the three things you browse on — silhouette, name, mature size. Everything
 * else the record carries (the nursery stages, the growth curves, the four seasonal palettes, the
 * phenology days, the LOD distances) is real design data that had nowhere to be seen.
 * This module turns a definition into that sheet, and deliberately does it *without touching the
 * DOM*, the same way `FloraFacets` holds the browse model: the card in `FloraCard.ts` only lays out
 * what these functions return, so the sheet can be tested in node and can never disagree with the
 * catalog.
 *
 * Nothing here is authored — every value is either read straight off the record or derived from it
 * by the same functions the scene uses (`computeGrowthState`, `estimateAgeYears`). A field the
 * record omits is reported as omitted rather than filled in with a plausible default; the point of
 * the sheet is to show what the data model actually holds.
 */

export type DisplayUnit = 'meters' | 'feet';

/** A single labelled row on the sheet. */
export interface Fact {
  label: string;
  value: string;
  /** Hex colour to swatch beside the value, or `null` when the fact is not a colour. */
  color: string | null;
  /** Magnitude in [0, 1] for a bar, or `null` when the fact is not a ratio. */
  meter: number | null;
  /** What the field means, for the row's tooltip. */
  hint: string | null;
  /** True when the record leaves this field out — shown greyed rather than hidden. */
  absent: boolean;
}

interface FactOptions {
  color?: string;
  meter?: number;
  hint?: string;
  absent?: boolean;
}

export interface FactSection {
  id: string;
  title: string;
  /** One line on what this block of the record drives. */
  note: string;
  facts: Fact[];
}

/** One season's appearance block, unpacked for the seasonal strip. */
export interface SeasonFacet {
  season: SimpleSeason;
  label: string;
  foliageColor: string;
  foliageDensity: number;
  flowerColor: string | null;
  flowerDensity: number;
  fruitColor: string | null;
  fruitDensity: number;
  branchVisibility: number;
  dormant: boolean;
}

/** A point on the growth curves, sampled through the same model the geometry runs. */
export interface GrowthSample {
  maturity: number;
  heightMeters: number;
  spreadMeters: number;
}

/** A nursery container stage, resolved to the fraction of mature height it represents. */
export interface StageRow {
  id: string;
  label: string;
  ageYears: number;
  heightMeters: number;
  spreadMeters: number;
  /** Height as a fraction of mature typical height, in [0, 1+]. */
  fractionOfMature: number;
}

export interface BloomBand {
  startDay: number;
  endDay: number;
  intensity: number;
  color: string;
  label: string;
}

export type YearEventKind = 'leaf-out' | 'autumn-color' | 'leaf-drop';

export interface YearEvent {
  kind: YearEventKind;
  day: number;
  label: string;
  dateLabel: string;
}

export interface FloraFactsheet {
  id: string;
  schemaVersion: number;
  /** Common name with cultivar, e.g. `Panicle hydrangea 'Limelight'`. */
  title: string;
  botanicalName: string;
  cultivar: string | null;
  genus: string;
  species: string;
  commonNames: string[];
  category: string;
  categoryLabel: string;
  formFactor: FormFactor;
  formFactorLabel: string;
  geometryForm: FloraGeometryForm;
  tags: string[];
  colorFamilies: { value: ColorFamily; label: string; swatch: string }[];
  sections: FactSection[];
  seasons: SeasonFacet[];
  stages: StageRow[];
  /** Height/spread sampled across maturity, for the growth plot. */
  curve: GrowthSample[];
  bloomBands: BloomBand[];
  yearEvents: YearEvent[];
  /** Mature typical dimensions, kept unformatted for the scale drawing. */
  matureHeightMeters: number;
  matureSpreadMeters: number;
  /** Easing names, labelled — the growth plot's legend names the curve it just drew. */
  heightCurveLabel: string;
  spreadCurveLabel: string;
  /** Years from the smallest nursery stage to typical mature height. */
  yearsToMaturity: number;
  evergreen: boolean;
}

/* -------------------------------------- Formatting ------------------------------------- */

const SEASON_LABELS: Record<SimpleSeason, string> = {
  spring: 'Spring',
  summer: 'Summer',
  autumn: 'Autumn',
  winter: 'Winter',
};

const GEOMETRY_FORM_LABELS: Record<FloraGeometryForm, string> = {
  'round-canopy-tree': 'Round-canopy tree',
  'conical-tree': 'Conical tree',
  'mounding-shrub': 'Mounding shrub',
  'grass-tuft': 'Grass tuft',
  'perennial-clump': 'Perennial clump',
  'groundcover-mat': 'Groundcover mat',
};

export function geometryFormLabel(form: FloraGeometryForm): string {
  return GEOMETRY_FORM_LABELS[form];
}

/** `full-sun` → `Full sun`. Used for the sunlight, moisture, and curve enums alike. */
export function enumLabel(value: string): string {
  const words = value.split('-').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function percentLabel(value: number): string {
  return `${Math.round(value * 100)}%`;
}

/** Trim trailing zeros so `0.50` reads as `0.5` and `1.00` as `1`. */
export function numberLabel(value: number, places = 2): string {
  return String(Number(value.toFixed(places)));
}

const EM_DASH = '—';

function fact(label: string, value: string, options: FactOptions = {}): Fact {
  return {
    label,
    value,
    color: options.color ?? null,
    meter: options.meter ?? null,
    hint: options.hint ?? null,
    absent: options.absent ?? false,
  };
}

/** A fact for a field the record may omit: absent fields keep their row, marked as unset. */
function optionalFact<T extends number | string>(
  label: string,
  value: T | undefined,
  format: (value: T) => string,
  options: FactOptions = {},
): Fact {
  if (value === undefined) return fact(label, EM_DASH, { ...options, absent: true });
  return fact(label, format(value), options);
}

/* --------------------------------------- Sections -------------------------------------- */

function identitySection(definition: FloraDefinition): FactSection {
  const zones = definition.hardinessZones;
  return {
    id: 'identity',
    title: 'Identity',
    note: 'What the catalog calls this plant and what it needs to live.',
    facts: [
      fact('Catalog id', definition.id, { hint: 'The key placements and saved sites refer to' }),
      fact('Schema version', String(definition.schemaVersion)),
      fact('Genus', genusOf(definition)),
      fact('Species', speciesEpithetOf(definition) || EM_DASH, {
        absent: speciesEpithetOf(definition) === '',
      }),
      optionalFact('Cultivar', definition.cultivar, (value: string) => `‘${value}’`),
      fact('Common names', definition.commonNames.join(', ') || EM_DASH, {
        absent: definition.commonNames.length === 0,
      }),
      fact('Category', categoryLabel(definition.category)),
      fact('Form factor', formFactorLabel(formFactorOf(definition)), {
        hint: 'Derived from the category — the palette’s primary axis',
      }),
      fact(
        'Hardiness zones',
        zones ? `${zones.minimum} – ${zones.maximum}` : EM_DASH,
        { absent: zones === undefined, hint: 'USDA zones this plant survives in' },
      ),
      fact('Sunlight', definition.sunlight.map(enumLabel).join(' · ') || EM_DASH, {
        absent: definition.sunlight.length === 0,
      }),
      fact('Moisture', definition.moisture.map(enumLabel).join(' · ') || EM_DASH, {
        absent: definition.moisture.length === 0,
      }),
    ],
  };
}

/**
 * How this plant reads *here* — the only section on the sheet that is not a property of the plant.
 *
 * Every figure is derived, never stored: the rating, the dates, the expected size all come from the
 * resolver combining this record with the site profile. Which is why the section simply vanishes on
 * an unlocalized site rather than showing rows marked absent — an absent field means the record does
 * not say, and none of this is the record's to say.
 */
function siteSection(resolved: ResolvedFlora, unit: DisplayUnit): FactSection {
  const { suitability, phenology, growth, nativeStatus } = resolved;
  const warning = primaryWarning(suitability);
  const facts: Fact[] = [
    fact('Suitability', suitabilityLabel(suitability.rating), {
      hint: 'How this site’s climate compares with the range this plant is grown in',
    }),
  ];
  if (warning) facts.push(fact(warning.headline, warning.message));
  facts.push(
    fact('Regional status', nativeStatusLabel(nativeStatus), {
      hint: 'Whether the plant is indigenous to this site’s region',
    }),
  );
  if (Number.isFinite(suitability.coldHeadroom)) {
    facts.push(
      fact('Winter margin', `${numberLabel(suitability.coldHeadroom, 1)} zones of headroom`, {
        hint: 'How far this site sits above the coldest winter the plant survives',
      }),
    );
  }
  facts.push(
    optionalFact('Leaf-out here', phenology.leafOutDay, formatDayOfYear),
    optionalFact('Leaf drop here', phenology.leafDropDay, formatDayOfYear),
  );
  if (phenology.bloomWindows.length > 0) {
    const first = phenology.bloomWindows[0] as { startDayOfYear: number; endDayOfYear: number };
    facts.push(
      fact('Bloom here', `${formatDayOfYear(first.startDayOfYear)} – ${formatDayOfYear(first.endDayOfYear)}`),
    );
  }
  if (phenology.fruitWindow) {
    facts.push(
      fact(
        'Fruit here',
        `${formatDayOfYear(phenology.fruitWindow.startDay)} – ${formatDayOfYear(phenology.fruitWindow.endDay)}`,
        { hint: 'Derived from this site’s bloom plus the record’s ripening and persistence' },
      ),
    );
  }
  facts.push(
    fact('Expected size here', `${formatLength(growth.matureHeightMeters, unit)} × ${formatLength(growth.matureSpreadMeters, unit)}`, {
      hint: growth.managed
        ? 'Set by pruning rather than by the climate'
        : 'A point inside the record’s own mature range, chosen by how comfortable this site is',
    }),
  );
  if (phenology.springShiftDays !== 0 || phenology.autumnShiftDays !== 0) {
    facts.push(
      fact(
        'Shift from reference',
        `${signed(phenology.springShiftDays)} days spring, ${signed(phenology.autumnShiftDays)} days autumn`,
        { hint: 'How far this site moves the catalog’s baseline calendar' },
      ),
    );
  }

  return {
    id: 'site',
    title: 'This site',
    note: 'Resolved for this site’s climate. Nothing here is stored — it is derived on demand.',
    facts,
  };
}

/** `+12` / `−4` / `0`, so a shift reads as a direction rather than as a bare number. */
function signed(days: number): string {
  return days > 0 ? `+${days}` : String(days);
}

/**
 * What the plant does for the wildlife around it.
 *
 * Values are the record's own and never move with the site; only the *months* do, which is why the
 * localized windows are shown beside them when there is a site to resolve against.
 */
function ecologySection(definition: FloraDefinition, resolved?: ResolvedFlora): FactSection {
  const ecology = definition.ecology as NonNullable<FloraDefinition['ecology']>;
  const facts: Fact[] = [];

  for (const resource of ecology.food ?? []) {
    const window = resolved?.ecology.food.find((entry) => entry.kind === resource.kind)?.window;
    const when = window
      ? `${formatDayOfYear(window.startDay)} – ${formatDayOfYear(window.endDay)}`
      : enumLabel(resource.phase);
    facts.push(
      fact(foodResourceLabel(resource.kind), when, {
        meter: resource.value,
        hint: resource.note ?? `For ${resource.consumers.map(wildlifeGroupLabel).join(', ').toLowerCase()}`,
      }),
    );
  }

  for (const resource of ecology.habitat ?? []) {
    facts.push(
      fact(habitatResourceLabel(resource.kind), enumLabel(resource.phase), {
        meter: resource.value,
        hint: resource.note ?? `Used by ${resource.users.map(wildlifeGroupLabel).join(', ').toLowerCase()}`,
      }),
    );
  }

  for (const relation of ecology.larvalHosts ?? []) {
    facts.push(
      fact(
        `Larval host — ${wildlifeGroupLabel(relation.group).toLowerCase()}`,
        relation.speciesCount === undefined ? 'Documented' : `${relation.speciesCount} species`,
        { hint: relation.note ?? 'Nectar feeds adults; only host foliage produces the next generation' },
      ),
    );
  }

  if (ecology.keystone === true) {
    facts.push(fact('Keystone genus', 'Yes', { hint: 'Carries a disproportionate share of the local food web' }));
  }
  if (ecology.deerResistant === true) {
    facts.push(fact('Deer resistant', 'Yes'));
  }
  facts.push(fact('Ecological value', percentLabel(ecologyScore(ecology)), { meter: ecologyScore(ecology) }));

  return {
    id: 'ecology',
    title: 'Ecology',
    note: resolved
      ? 'Resources this plant offers, dated against this site’s own bloom and fruiting.'
      : 'Resources this plant offers. Localize the site to see when they fall here.',
    facts,
  };
}

function growthSection(definition: FloraDefinition, unit: DisplayUnit): FactSection {
  const { growth } = definition;
  const height = growth.matureHeightMeters;
  const spread = growth.matureSpreadMeters;
  const radius = spread.typical / 2;

  return {
    id: 'growth',
    title: 'Growth',
    note: 'Drives the size of every placement, at every maturity.',
    facts: [
      fact(
        'Mature height',
        `${formatLength(height.typical, unit)}  (${formatLength(height.minimum, unit)} – ${formatLength(height.maximum, unit)})`,
        { hint: 'Typical, with the minimum–maximum range the model extrapolates into' },
      ),
      fact(
        'Mature spread',
        `${formatLength(spread.typical, unit)}  (${formatLength(spread.minimum, unit)} – ${formatLength(spread.maximum, unit)})`,
      ),
      fact('Mature canopy', formatFootprint(Math.PI * radius * radius, unit), {
        hint: 'Ground one grown specimen covers',
      }),
      optionalFact(
        'Annual growth',
        growth.annualGrowthRateMeters,
        (value: number) => `${formatLength(value, unit)} / year`,
      ),
      fact('Years to maturity', formatAge(estimateAgeYears(growth, 1)), {
        hint: 'Interpolated from the nursery stages and the growth rate',
      }),
      fact('Height curve', enumLabel(growth.heightCurve)),
      fact('Spread curve', enumLabel(growth.spreadCurve)),
      optionalFact('Trunk curve', growth.trunkCurve, enumLabel, {
        hint: 'Allometric trunks thicken faster than they climb',
      }),
      optionalFact('Density curve', growth.densityCurve, enumLabel, {
        hint: 'How foliage fills in as the plant matures',
      }),
      fact('Nursery stages', String(growth.containerStages.length), {
        hint: 'Container sizes this plant can be specified at',
      }),
    ],
  };
}

function seasonSection(definition: FloraDefinition): FactSection {
  const { seasons } = definition;
  return {
    id: 'phenology',
    title: 'Phenology',
    note: 'The calendar the seasonal appearance is interpolated along.',
    facts: [
      fact('Evergreen', seasons.evergreen ? 'Yes' : 'No', {
        hint: 'Evergreens keep foliage through the winter block',
      }),
      optionalFact('Leaf out', seasons.leafOutDay, dayLabel),
      optionalFact('Autumn colour', seasons.autumnColorStartDay, dayLabel),
      optionalFact('Leaf drop', seasons.leafDropDay, dayLabel),
      fact('Bloom windows', String(seasons.bloomWindows?.length ?? 0), {
        absent: (seasons.bloomWindows?.length ?? 0) === 0,
      }),
    ],
  };
}

function placementSection(definition: FloraDefinition, unit: DisplayUnit): FactSection {
  const { placement } = definition;
  return {
    id: 'placement',
    title: 'Placement',
    note: 'What the editor checks when you put one down.',
    facts: [
      fact('Footprint radius', formatLength(placement.footprintRadiusMeters, unit), {
        hint: 'Mature half-spread used for overlap warnings',
      }),
      fact('Recommended spacing', formatLength(placement.recommendedSpacingMeters, unit), {
        hint: 'Centre to centre, at maturity',
      }),
      fact('Maximum slope', `${numberLabel(placement.maxSlopeDegrees, 1)}°`, {
        hint: 'Steeper ground than this warns on placement',
      }),
      fact(
        'Density at spacing',
        `${numberLabel(densityPerHundred(placement.recommendedSpacingMeters, unit), 1)} per ${unit === 'feet' ? '1000 ft²' : '100 m²'}`,
        { hint: 'How many fit in a bed at the recommended spacing' },
      ),
    ],
  };
}

function renderingSection(definition: FloraDefinition, unit: DisplayUnit): FactSection {
  const { rendering } = definition;
  const [medium, low] = rendering.lodDistancesMeters;
  return {
    id: 'rendering',
    title: 'Rendering',
    note: 'How the plant is built and when it simplifies.',
    facts: [
      fact('Geometry form', geometryFormLabel(rendering.geometryForm), {
        hint: 'The procedural archetype the mesh is grown from',
      }),
      optionalFact('Trunk colour', rendering.trunkColor, (value: string) => value.toUpperCase(), {
        ...(rendering.trunkColor ? { color: rendering.trunkColor } : {}),
      }),
      fact('Medium LOD from', formatLength(medium, unit), {
        hint: 'Camera distance at which the detailed mesh is dropped',
      }),
      fact('Low LOD from', formatLength(low, unit)),
      fact('Impostor from', formatLength(rendering.impostorDistanceMeters, unit), {
        hint: 'Beyond this the plant is drawn as a billboard',
      }),
    ],
  };
}

/* --------------------------------------- Helpers --------------------------------------- */

const SQUARE_FEET_PER_SQUARE_METER = 10.7639;

function formatFootprint(squareMeters: number, unit: DisplayUnit): string {
  const value = unit === 'feet' ? squareMeters * SQUARE_FEET_PER_SQUARE_METER : squareMeters;
  const suffix = unit === 'feet' ? 'ft²' : 'm²';
  return value < 10 ? `${value.toFixed(1)} ${suffix}` : `${Math.round(value)} ${suffix}`;
}

/** Plants per 100 m² (or per 1000 ft²) on a square grid at the given spacing. */
function densityPerHundred(spacingMeters: number, unit: DisplayUnit): number {
  if (spacingMeters <= 0) return 0;
  const perSquareMeter = 1 / (spacingMeters * spacingMeters);
  return unit === 'feet'
    ? perSquareMeter * (1000 / SQUARE_FEET_PER_SQUARE_METER)
    : perSquareMeter * 100;
}

function dayLabel(day: number): string {
  return `${formatDayOfYear(day)}  ·  day ${Math.round(day)}`;
}

function seasonFacet(season: SimpleSeason, appearance: SeasonalAppearance): SeasonFacet {
  return {
    season,
    label: SEASON_LABELS[season],
    foliageColor: appearance.foliageColor,
    foliageDensity: appearance.foliageDensity,
    flowerColor: appearance.flowerColor ?? null,
    flowerDensity: appearance.flowerDensity ?? 0,
    fruitColor: appearance.fruitColor ?? null,
    fruitDensity: appearance.fruitDensity ?? 0,
    branchVisibility: appearance.branchVisibility,
    dormant: appearance.dormant,
  };
}

/** Nursery container labels, matching the manifest's wording. */
const STAGE_LABELS: Record<string, string> = {
  '1gal': '#1 container',
  '2gal': '#2 container',
  '3gal': '#3 container',
  '5gal': '#5 container',
  '7gal': '#7 container',
  '15gal': '#15 container',
  field: 'Field grown',
  mature: 'Mature specimen',
};

function stageRow(stage: ContainerStage, matureHeightMeters: number): StageRow {
  return {
    id: stage.id,
    label: STAGE_LABELS[stage.id] ?? stage.id,
    ageYears: stage.approximateAgeYears,
    heightMeters: stage.heightMeters,
    spreadMeters: stage.spreadMeters,
    fractionOfMature: matureHeightMeters > 0 ? stage.heightMeters / matureHeightMeters : 0,
  };
}

/**
 * Sample the growth model across maturity for the plot. Runs the real
 * {@link computeGrowthState}, so the drawn curve is the curve the scene grows along — including the
 * fact that height and spread follow different easings.
 */
export function growthSamples(definition: FloraDefinition, steps = 24): GrowthSample[] {
  const samples: GrowthSample[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const maturity = i / steps;
    const state = computeGrowthState(definition.growth, maturity);
    samples.push({
      maturity,
      heightMeters: state.heightMeters,
      spreadMeters: state.spreadMeters,
    });
  }
  return samples;
}

function bloomBands(definition: FloraDefinition): BloomBand[] {
  return (definition.seasons.bloomWindows ?? []).map((window) => ({
    startDay: window.startDayOfYear,
    endDay: window.endDayOfYear,
    intensity: window.intensity,
    color: window.color,
    label: `${formatDayOfYear(window.startDayOfYear)} – ${formatDayOfYear(window.endDayOfYear)} · ${percentLabel(window.intensity)} intensity`,
  }));
}

function yearEvents(definition: FloraDefinition): YearEvent[] {
  const { seasons } = definition;
  const events: YearEvent[] = [];
  const push = (kind: YearEventKind, day: number | undefined, label: string): void => {
    if (day === undefined) return;
    events.push({ kind, day, label, dateLabel: formatDayOfYear(day) });
  };
  push('leaf-out', seasons.leafOutDay, 'Leaf out');
  push('autumn-color', seasons.autumnColorStartDay, 'Autumn colour');
  push('leaf-drop', seasons.leafDropDay, 'Leaf drop');
  return events.sort((a, b) => a.day - b.day);
}

/* ------------------------------------- Entry point ------------------------------------- */

/** Read a catalog record back as a specification sheet. Pure — safe to call per render. */
export function buildFloraFactsheet(
  definition: FloraDefinition,
  unit: DisplayUnit = 'meters',
  resolved?: ResolvedFlora,
): FloraFactsheet {
  const matureHeightMeters = definition.growth.matureHeightMeters.typical;

  return {
    id: definition.id,
    schemaVersion: definition.schemaVersion,
    title: fullDisplayName(definition),
    botanicalName: definition.botanicalName,
    cultivar: definition.cultivar ?? null,
    genus: genusOf(definition),
    species: speciesEpithetOf(definition),
    commonNames: [...definition.commonNames],
    category: definition.category,
    categoryLabel: categoryLabel(definition.category),
    formFactor: formFactorOf(definition),
    formFactorLabel: formFactorLabel(formFactorOf(definition)),
    geometryForm: definition.rendering.geometryForm,
    tags: [...definition.tags],
    colorFamilies: colorFamiliesOf(definition).map((family) => ({
      value: family,
      label: colorFamilyLabel(family),
      swatch: COLOR_FAMILY_SWATCHES[family],
    })),
    sections: [
      identitySection(definition),
      // Before growth, because "will it live here" is the question asked ahead of "how big".
      ...(resolved ? [siteSection(resolved, unit)] : []),
      growthSection(definition, unit),
      seasonSection(definition),
      ...(definition.ecology ? [ecologySection(definition, resolved)] : []),
      placementSection(definition, unit),
      renderingSection(definition, unit),
    ],
    seasons: SIMPLE_SEASONS.map((season) => seasonFacet(season, definition.seasons[season])),
    stages: definition.growth.containerStages.map((stage) => stageRow(stage, matureHeightMeters)),
    curve: growthSamples(definition),
    bloomBands: bloomBands(definition),
    yearEvents: yearEvents(definition),
    matureHeightMeters,
    matureSpreadMeters: definition.growth.matureSpreadMeters.typical,
    heightCurveLabel: enumLabel(definition.growth.heightCurve),
    spreadCurveLabel: enumLabel(definition.growth.spreadCurve),
    yearsToMaturity: estimateAgeYears(definition.growth, 1),
    evergreen: definition.seasons.evergreen,
  };
}
